import { useEffect, useMemo, useRef } from "react";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";

// Buckets the inbox into Today / Yesterday / Earlier so a long-lived list
// stays scannable. All times are local to the viewer.
function bucketLabel(iso) {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return "Today";
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return "Earlier";
}

// "5m ago", "3h ago", "2d ago"
function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function kindBg(kind, unread) {
  const base = (() => {
    if (kind === "success") return "#15201a";
    if (kind === "warn")    return "#201a10";
    if (kind === "error")   return COLOR.roseDeep;
    return COLOR.soil;
  })();
  // Slightly brighter for unread so the user can scan at a glance.
  return unread ? base : COLOR.deep;
}
function kindBorder(kind, unread) {
  const base = (() => {
    if (kind === "success") return "#2a3a2e";
    if (kind === "warn")    return "#3a2e1e";
    if (kind === "error")   return COLOR.roseDim;
    return COLOR.edge;
  })();
  return unread ? base : COLOR.soil;
}

/**
 * Slide-down inbox panel. Fixed at the top of the same 480px shell as the
 * rest of the app. Tap × or the backdrop to close.
 *
 * Read state policy (from spec): unread rows are shown + count toward the
 * badge; read rows stay visible (greyed out) but don't count; dismiss/clear
 * hard-deletes. Auto-expire at 30 days happens server-side (cron, future).
 *
 * Opening the panel auto-marks everything read so the badge clears as soon
 * as the user has acknowledged the existence of the notifications. They can
 * still scroll back through them.
 */
export default function NotificationsPanel({
  notifications,
  loading,
  unreadCount,
  markAllRead,
  dismiss,
  clearAll,
  onClose,
  // onOpen(targetKind, targetId) — called when the user taps a row that
  // carries a deep-link target. App.jsx routes the tap to the right tab
  // (currently only 'cook_log' → Cookbook). If omitted, rows are plain.
  onOpen,
  // Synthetic pinned entries injected at the top of the list. Used for
  // app-level context that isn't a real notifications-table row — the
  // release-notes pin is the canonical example. Each entry carries its
  // own onTap + onDismiss so the parent owns its lifecycle (we don't
  // dismiss a synthetic via the notifications table).
  //   { id, emoji, msg, sublabel, kind, onTap, onDismiss }
  pinned = [],
  // Always-available footer affordance to open the full release notes
  // modal. Wired even when no pin is showing — lets users come back to
  // past notes while perusing the inbox.
  onOpenReleaseNotes,
}) {
  // Auto-mark-read on open. We deliberately fire this only on mount —
  // markAllRead's identity changes every render, but we don't want to
  // re-stamp every time the inbox list mutates.
  const markAllReadRef = useRef(markAllRead);
  useEffect(() => { markAllReadRef.current = markAllRead; }, [markAllRead]);
  const hadUnreadOnOpen = useRef(unreadCount > 0);
  useEffect(() => {
    if (hadUnreadOnOpen.current) markAllReadRef.current?.();
  }, []);

  // Escape-key dismiss. Without this, mouse/keyboard users have to
  // hunt for the × or click the narrow backdrop strip below the
  // panel to close. ESC is the universal escape hatch.
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const grouped = useMemo(() => {
    const out = [];
    let lastBucket = null;
    for (const n of notifications) {
      const b = bucketLabel(n.created_at);
      if (b !== lastBucket) {
        out.push({ kind: "header", label: b });
        lastBucket = b;
      }
      out.push({ kind: "row", row: n });
    }
    return out;
  }, [notifications]);

  return (
    <>
      {/* Backdrop — full-screen, slightly darkened, tap-anywhere to
          close. Used to be barely visible; bumped alpha so users
          actually see they can tap outside the panel to dismiss. */}
      <button
        onClick={onClose}
        aria-label="Close notifications"
        style={{
          position: "fixed", inset: 0, zIndex: 100,
          background: "rgba(0,0,0,0.55)", border: "none", padding: 0, cursor: "pointer",
        }}
      />

      <div
        style={{
          position: "fixed",
          top: 0, left: 0, right: 0,
          maxWidth: 480, margin: "0 auto",
          maxHeight: "85vh",
          // Was COLOR.deep (0.42 alpha) — too transparent, content
          // behind bled through and made the panel feel ghostly.
          // COLOR.night is the opaque cream page-fill used by every
          // other full-overlay screen (UserProfile, Settings, etc.)
          // so the panel matches their solidity.
          background: COLOR.night,
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
          border: `1px solid ${COLOR.border}`,
          borderTop: "none",
          zIndex: 110,
          display: "flex", flexDirection: "column",
          boxShadow: "0 12px 36px rgba(0,0,0,0.45)",
          animation: "panelIn 0.22s ease",
        }}
      >
        <style>{`@keyframes panelIn { from { transform: translateY(-12px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }`}</style>

        {/* header — paddingTop includes env(safe-area-inset-top) so
            the title + × clear the iOS notch / Dynamic Island. The
            panel itself sits at top:0 (glass extends under the
            chrome) so the safe-area lives inside the header. */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "calc(16px + env(safe-area-inset-top)) 18px 12px",
        }}>
          <div style={{ fontFamily: FONT.serif, fontSize: 20, color: COLOR.ink }}>
            Notifications
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
            style={{
              width: 36, height: 36, borderRadius: 18,
              background: COLOR.soil, border: `1px solid ${COLOR.border}`,
              color: COLOR.dim, fontSize: 18, lineHeight: 1,
              cursor: "pointer", padding: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {/* action row */}
        {notifications.length > 0 && (
          <div style={{
            display: "flex", gap: 8, padding: "0 18px 10px",
            borderBottom: `1px solid ${COLOR.soil}`,
            paddingBottom: 12,
          }}>
            <button
              onClick={clearAll}
              style={{
                background: "transparent", border: `1px solid ${COLOR.border}`,
                color: COLOR.dim, borderRadius: 8, padding: "6px 10px",
                fontFamily: FONT.mono, fontSize: 10,
                letterSpacing: "0.08em", cursor: "pointer",
              }}
            >
              CLEAR ALL
            </button>
          </div>
        )}

        {/* list */}
        <div style={{ overflowY: "auto", padding: "8px 12px 16px", flex: 1 }}>
          {loading && (
            <div style={{ padding: "32px 16px", textAlign: "center", color: COLOR.muted,
                          fontFamily: FONT.sans, fontSize: 13 }}>
              Loading…
            </div>
          )}
          {!loading && notifications.length === 0 && pinned.length === 0 && (
            <div style={{ padding: "40px 16px", textAlign: "center", color: COLOR.muted,
                          fontFamily: FONT.sans, fontSize: 13 }}>
              <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.5 }}>🔔</div>
              No notifications yet. When family members add to the pantry,
              update the shopping list, or schedule meals, you'll see it here.
            </div>
          )}

          {/* Pinned synthetic entries — release notes, future system
              messages. Always render at the top of the list, before
              the time-bucketed real notifications. The parent decides
              when to populate this (today: only when there's an
              unseen release). */}
          {pinned.map(p => (
            <div
              key={p.id}
              style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "11px 12px",
                marginBottom: 6,
                background: COLOR.skyDeep,
                border: `1px solid ${COLOR.skyBorder}`,
                borderRadius: 10,
              }}
            >
              <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1.2 }}>
                {p.emoji || "📋"}
              </span>
              <button
                onClick={p.onTap}
                style={{
                  flex: 1, minWidth: 0,
                  background: "transparent", border: "none", padding: 0,
                  textAlign: "left", cursor: "pointer", color: "inherit",
                  display: "flex", alignItems: "flex-start", gap: 10,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: FONT.sans, fontSize: 13,
                    color: COLOR.ink, lineHeight: 1.35,
                  }}>
                    {p.msg}
                  </div>
                  {p.sublabel && (
                    <div style={{
                      fontFamily: FONT.mono, fontSize: 9,
                      color: COLOR.sky, letterSpacing: "0.08em", marginTop: 4,
                    }}>
                      {p.sublabel}
                    </div>
                  )}
                </div>
                <span
                  aria-hidden
                  style={{
                    fontFamily: FONT.mono, fontSize: 14,
                    color: COLOR.sky, letterSpacing: "0.04em",
                    flexShrink: 0, alignSelf: "center",
                  }}
                >
                  →
                </span>
              </button>
              {p.onDismiss && (
                <button
                  onClick={p.onDismiss}
                  aria-label="Dismiss"
                  style={{
                    background: "transparent", border: "none", color: COLOR.sub,
                    fontSize: 16, lineHeight: 1, cursor: "pointer", padding: 4,
                    flexShrink: 0,
                  }}
                  title="Dismiss"
                >
                  ×
                </button>
              )}
            </div>
          ))}

          {grouped.map((entry, i) => {
            if (entry.kind === "header") {
              return (
                <div key={`h-${entry.label}-${i}`} style={{
                  fontFamily: FONT.mono, fontSize: 9,
                  color: COLOR.muted, letterSpacing: "0.12em",
                  padding: "12px 6px 6px",
                }}>
                  {entry.label.toUpperCase()}
                </div>
              );
            }
            const n = entry.row;
            const unread = !n.read_at;
            // A notification becomes a link when it carries both a target
            // kind and a target id AND the parent gave us an onOpen handler
            // for that kind. Otherwise it's a plain info row.
            const linkable = !!(onOpen && n.target_kind && n.target_id);
            const openTarget = () => {
              if (!linkable) return;
              onOpen(n.target_kind, n.target_id);
              // App.jsx closes the panel after routing; we don't close
              // preemptively here so the navigation has time to happen.
            };
            return (
              <div
                key={n.id}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "11px 12px",
                  marginBottom: 6,
                  background: kindBg(n.kind, unread),
                  border: `1px solid ${kindBorder(n.kind, unread)}`,
                  borderRadius: 10,
                  opacity: unread ? 1 : 0.7,
                }}
              >
                <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1.2 }}>
                  {n.emoji || "🔔"}
                </span>
                {/* Body is a button when linkable so the whole message + chevron
                    are one target; stays a div when not linkable so there's no
                    pointer hint where nothing happens. */}
                {linkable ? (
                  <button
                    onClick={openTarget}
                    style={{
                      flex: 1, minWidth: 0,
                      background: "transparent", border: "none", padding: 0,
                      textAlign: "left", cursor: "pointer", color: "inherit",
                      display: "flex", alignItems: "flex-start", gap: 10,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: FONT.sans, fontSize: 13,
                        color: COLOR.ink, lineHeight: 1.35,
                      }}>
                        {n.msg}
                      </div>
                      <div style={{
                        fontFamily: FONT.mono, fontSize: 9,
                        color: COLOR.muted, letterSpacing: "0.08em", marginTop: 4,
                      }}>
                        {timeAgo(n.created_at).toUpperCase()}
                      </div>
                    </div>
                    <span
                      aria-hidden
                      style={{
                        fontFamily: FONT.mono, fontSize: 14,
                        color: COLOR.gold, letterSpacing: "0.04em",
                        flexShrink: 0, alignSelf: "center",
                      }}
                    >
                      →
                    </span>
                  </button>
                ) : (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontFamily: FONT.sans, fontSize: 13,
                      color: COLOR.ink, lineHeight: 1.35,
                    }}>
                      {n.msg}
                    </div>
                    <div style={{
                      fontFamily: FONT.mono, fontSize: 9,
                      color: COLOR.muted, letterSpacing: "0.08em", marginTop: 4,
                    }}>
                      {timeAgo(n.created_at).toUpperCase()}
                    </div>
                  </div>
                )}
                <button
                  onClick={() => dismiss(n.id)}
                  aria-label="Dismiss"
                  style={{
                    background: "transparent", border: "none", color: COLOR.sub,
                    fontSize: 16, lineHeight: 1, cursor: "pointer", padding: 4,
                    flexShrink: 0,
                  }}
                  title="Dismiss"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer link to the full release-notes modal. Always visible
            (even without a pinned entry) so users can browse back
            through past notes while perusing the inbox. */}
        {onOpenReleaseNotes && (
          <div style={{
            borderTop: `1px solid ${COLOR.soil}`,
            padding: "10px 18px 14px",
          }}>
            <button
              onClick={onOpenReleaseNotes}
              style={{
                width: "100%",
                background: "transparent", border: `1px solid ${COLOR.skyBorder}`,
                color: COLOR.sky, borderRadius: 10,
                padding: "10px 12px",
                fontFamily: FONT.mono, fontSize: 10,
                letterSpacing: "0.1em", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}
            >
              <span>📋 RELEASE NOTES · WHAT'S CHANGED</span>
              <span>→</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
}
